import {COLOR_HEX, LineMark, Mark, MarkColor, MarkSize, STICKERS, StickerKind, StickerMark, StrokeMark, TextMark} from './markup';

/**
 * Draws a MarkupDoc's marks onto a 2D canvas in the PHOTO's pixel space
 * (width × height, the photo as rotated). One renderer serves the editor's
 * screen (through a view transform) and the burned-in copy at full
 * resolution, so what the person sees is exactly what gets saved.
 *
 * The web copy of Android's `MarkupRenderer.kt`: the same sizes, shapes and
 * outlines, number for number, so a photo looks the same whichever app
 * saved it. Change the two together.
 */

export interface Rect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export interface Point {
  x: number;
  y: number;
}

export const PLACEHOLDER = 'Type here';

/** The outline around text and stickers: thin black for every color. */
export const OUTLINE = 'rgba(0,0,0,0.902)';

/** Text outline stroke, as a share of the text size (half shows outside). */
export const OUTLINE_TEXT = 0.11;

/** Bold Roboto, as Android's DEFAULT_BOLD; the PWA loads the 700 weight. */
export const TEXT_FONT_FAMILY = 'Roboto, "Helvetica Neue", Arial, sans-serif';

/** Roboto's line height (descent − ascent) and ascent, per unit of text size. */
const LINE_HEIGHT = 1.171875;
const ASCENT = 0.927734375;

const CONE_ORANGE = '#FF6D00';
const CONE_BASE = '#E65100';
const HAZARD_YELLOW = '#FFD600';
const STOP_RED = '#D50000';
const INK = '#1A1A1A';
const WATER_BLUE = '#1E88E5';
const SPRINKLER_BODY = '#37474F';
const SPRINKLER_CAP = '#78909C';
const WHITE = '#FFFFFF';

// Material "pan_tool" (Apache 2.0), a raised hand in a 24-unit box.
const HAND_PATH =
  'M23,5.5V20c0,2.2 -1.8,4 -4,4h-7.3c-1.08,0 -2.1,-0.43 -2.85,-1.19L1,14.83' +
  'c0,0 1.26,-1.23 1.3,-1.25c0.22,-0.19 0.49,-0.29 0.79,-0.29c0.22,0 0.42,0.06 0.6,0.16' +
  'C3.73,13.46 8,15.91 8,15.91V4c0,-0.83 0.67,-1.5 1.5,-1.5S11,3.17 11,4v7h1V1.5' +
  'C12,0.67 12.67,0 13.5,0S15,0.67 15,1.5V11h1V2.5C16,1.67 16.67,1 17.5,1S19,1.67 19,2.5' +
  'V11h1V5.5C20,4.67 20.67,4 21.5,4S23,4.67 23,5.5z';

let handPath: Path2D | null = null;

/** How a sticker is turned as drawn: upright ones never are. */
export const stickerAngle = (m: StickerMark): number => STICKERS[m.kind].rotatable ? m.angle : 0;

/** Arrows a size smaller, X half, cut lines a fifth shorter, sprinklers a quarter smaller. */
const STICKER_KIND_SIZE: Partial<Record<StickerKind, number>> = {arrow: 0.8, x: 0.5, cut: 0.8, sprinkler: 0.75};

export const stickerPreset = (size: MarkSize, kind: StickerKind): number =>
  ({s: 0.12, m: 0.2, l: 0.32}[size]) * (STICKER_KIND_SIZE[kind] ?? 1);

/** Clamps a sticker's true scale. No real floor: one placed far zoomed in is truly that small. */
export const clampScale = (s: number): number => Math.min(1.2, Math.max(0.001, s));

/** A mark's added-at zoom, guarded against hand-edited documents. */
const addedZoom = (z: number): number => Number.isFinite(z) ? Math.max(1, z) : 1;

/** Rotates a photo-space point into a mark's local (unrotated) frame. */
export const toLocal = (x: number, y: number, cx: number, cy: number, angleDeg: number): Point => {
  const a = -angleDeg * Math.PI / 180;
  const dx = x - cx;
  const dy = y - cy;
  return {x: dx * Math.cos(a) - dy * Math.sin(a), y: dx * Math.sin(a) + dy * Math.cos(a)};
};

/** The inverse of toLocal. */
export const toPhoto = (lx: number, ly: number, cx: number, cy: number, angleDeg: number): Point => {
  const a = angleDeg * Math.PI / 180;
  return {x: cx + lx * Math.cos(a) - ly * Math.sin(a), y: cy + lx * Math.sin(a) + ly * Math.cos(a)};
};

/** Clockwise degrees from "straight up" of the vector center → point. */
export const angleFromUp = (cx: number, cy: number, x: number, y: number): number =>
  Math.atan2(x - cx, -(y - cy)) * 180 / Math.PI;

export const distToSegment = (px: number, py: number, ax: number, ay: number, bx: number, by: number): number => {
  const vx = bx - ax;
  const vy = by - ay;
  const len2 = vx * vx + vy * vy;
  const t = len2 === 0 ? 0 : Math.min(1, Math.max(0, ((px - ax) * vx + (py - ay) * vy) / len2));
  return Math.hypot(px - (ax + t * vx), py - (ay + t * vy));
};

export const rectContains = (r: Rect, x: number, y: number): boolean =>
  x >= r.left && x < r.right && y >= r.top && y < r.bottom;

export const inset = (r: Rect, dx: number, dy: number = dx): Rect =>
  ({left: r.left + dx, top: r.top + dy, right: r.right - dx, bottom: r.bottom - dy});

/** Text width measurement: a shared offscreen canvas, or an estimate where there is none (tests). */
export type Measure = (text: string, sizePx: number) => number;

let measureCtx: CanvasRenderingContext2D | null | undefined;
export const canvasMeasure: Measure = (text, sizePx) => {
  if (measureCtx === undefined) {
    try {
      measureCtx = document.createElement('canvas').getContext('2d');
    } catch {
      measureCtx = null;
    }
  }
  if (!measureCtx) return text.length * sizePx * 0.58;
  measureCtx.font = `bold ${sizePx}px ${TEXT_FONT_FAMILY}`;
  return measureCtx.measureText(text).width;
};

/**
 * Lines of [text] wrapped the way Android's StaticLayout does: at spaces, a
 * word longer than the line broken by letters, newlines kept. [fits] says
 * whether a run of text fits the box width.
 */
export const wrapText = (text: string, fits: (s: string) => boolean): string[] => {
  const lines: string[] = [];
  // Pushes all but the last letters-piece of a too-long word; returns the last.
  const breakWord = (word: string): string => {
    let piece = '';
    for (const ch of Array.from(word)) {
      if (piece && !fits(piece + ch)) {
        lines.push(piece);
        piece = ch;
      } else {
        piece += ch;
      }
    }
    return piece;
  };
  text.split('\n').forEach(paragraph => {
    let line: string | null = null;
    paragraph.split(' ').forEach(word => {
      if (line === null) {
        line = fits(word) ? word : breakWord(word);
        return;
      }
      const candidate: string = `${line} ${word}`;
      if (fits(candidate.trimEnd())) {
        line = candidate;
      } else {
        lines.push(line);
        line = fits(word) ? word : breakWord(word);
      }
    });
    lines.push(line ?? '');
  });
  return lines;
};

export interface TextLayout {
  lines: string[];
  /** The wrap width (photo px). */
  width: number;
  height: number;
  size: number;
  lineHeight: number;
}

const strokePath = (ctx: CanvasRenderingContext2D, path: Path2D, color: string, width: number) => {
  ctx.strokeStyle = color;
  ctx.lineWidth = width;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.stroke(path);
};

const fillPath = (ctx: CanvasRenderingContext2D, path: Path2D, color: string) => {
  ctx.fillStyle = color;
  ctx.fill(path);
};

const roundRect = (x0: number, y0: number, x1: number, y1: number, r: number): Path2D => {
  const p = new Path2D();
  p.moveTo(x0 + r, y0);
  p.lineTo(x1 - r, y0);
  p.arcTo(x1, y0, x1, y0 + r, r);
  p.lineTo(x1, y1 - r);
  p.arcTo(x1, y1, x1 - r, y1, r);
  p.lineTo(x0 + r, y1);
  p.arcTo(x0, y1, x0, y1 - r, r);
  p.lineTo(x0, y0 + r);
  p.arcTo(x0, y0, x0 + r, y0, r);
  p.closePath();
  return p;
};

/** A closed polygon with its corners rounded by [r] (Android's CornerPathEffect). */
const roundedPolygon = (pts: [number, number][], r: number): Path2D => {
  const p = new Path2D();
  const n = pts.length;
  const mid = (a: [number, number], b: [number, number]): [number, number] => [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
  const start = mid(pts[n - 1], pts[0]);
  p.moveTo(start[0], start[1]);
  for (let i = 0; i < n; i++) {
    const c = pts[i];
    const next = pts[(i + 1) % n];
    p.arcTo(c[0], c[1], next[0], next[1], r);
  }
  p.closePath();
  return p;
};

const polygon = (pts: [number, number][]): Path2D => {
  const p = new Path2D();
  pts.forEach(([x, y], i) => i === 0 ? p.moveTo(x, y) : p.lineTo(x, y));
  p.closePath();
  return p;
};

/**
 * Draws a sticker in a unit box centered on the origin (−0.5…0.5); the
 * caller has already translated, rotated and scaled. Also used for the
 * tray's previews.
 */
export const drawStickerUnit = (ctx: CanvasRenderingContext2D, kind: StickerKind, color: string) => {
  const halo = OUTLINE;
  switch (kind) {
    case 'arrow': {
      // Pointing right; the turn handle aims it.
      const shaft = new Path2D();
      shaft.moveTo(-0.46, 0);
      shaft.lineTo(0.12, 0);
      const head = polygon([[0.48, 0], [0.06, -0.22], [0.06, 0.22]]);
      strokePath(ctx, shaft, halo, 0.16);
      strokePath(ctx, head, halo, 0.05);
      fillPath(ctx, head, halo);
      strokePath(ctx, shaft, color, 0.11);
      fillPath(ctx, head, color);
      break;
    }
    case 'cut': {
      // A cut-here line: one solid straight line.
      const p = new Path2D();
      p.moveTo(-0.44, 0);
      p.lineTo(0.44, 0);
      strokePath(ctx, p, halo, 0.095);
      strokePath(ctx, p, color, 0.06);
      break;
    }
    case 'x': {
      const p = new Path2D();
      p.moveTo(-0.36, -0.36);
      p.lineTo(0.36, 0.36);
      p.moveTo(0.36, -0.36);
      p.lineTo(-0.36, 0.36);
      strokePath(ctx, p, halo, 0.19);
      strokePath(ctx, p, color, 0.13);
      break;
    }
    case 'circle': {
      const p = new Path2D();
      p.arc(0, 0, 0.42, 0, Math.PI * 2);
      strokePath(ctx, p, halo, 0.105);
      strokePath(ctx, p, color, 0.07);
      break;
    }
    case 'check': {
      const p = new Path2D();
      p.moveTo(-0.38, 0.02);
      p.lineTo(-0.12, 0.3);
      p.lineTo(0.4, -0.3);
      strokePath(ctx, p, halo, 0.19);
      strokePath(ctx, p, color, 0.13);
      break;
    }
    case 'sprinkler': {
      // A pop-up sprinkler head throwing water out to both sides, arcing up
      // and falling (no jet straight up).
      const spray = new Path2D();
      [1, -1].forEach(side => {
        spray.moveTo(0, -0.04);
        spray.quadraticCurveTo(side * 0.24, -0.52, side * 0.45, 0.06);
        spray.moveTo(0, -0.04);
        spray.quadraticCurveTo(side * 0.13, -0.38, side * 0.27, -0.04);
      });
      strokePath(ctx, spray, halo, 0.085);
      strokePath(ctx, spray, WATER_BLUE, 0.05);
      ([[0.45, 0.16], [0.27, 0.05]] as [number, number][]).forEach(([x, y]) => {
        [x, -x].forEach(dx => {
          const outer = new Path2D();
          outer.arc(dx, y, 0.05, 0, Math.PI * 2);
          fillPath(ctx, outer, halo);
          const inner = new Path2D();
          inner.arc(dx, y, 0.035, 0, Math.PI * 2);
          fillPath(ctx, inner, WATER_BLUE);
        });
      });
      const parts: [Path2D, string][] = [
        [roundRect(-0.15, 0.17, 0.15, 0.46, 0.03), SPRINKLER_BODY],
        [roundRect(-0.2, 0.08, 0.2, 0.19, 0.03), SPRINKLER_CAP],
        [roundRect(-0.06, -0.06, 0.06, 0.1, 0.03), SPRINKLER_CAP],
      ];
      parts.forEach(([rect, c]) => {
        fillPath(ctx, rect, c);
        strokePath(ctx, rect, INK, 0.03);
      });
      break;
    }
    case 'cone': {
      const half = (y: number) => 0.06 + (y + 0.46) / 0.78 * 0.25;
      const body = polygon([[-half(-0.46), -0.46], [half(-0.46), -0.46], [half(0.32), 0.32], [-half(0.32), 0.32]]);
      fillPath(ctx, body, CONE_ORANGE);
      ([[-0.22, -0.11], [0.04, 0.15]] as [number, number][]).forEach(([t, b]) => {
        fillPath(ctx, polygon([[-half(t), t], [half(t), t], [half(b), b], [-half(b), b]]), WHITE);
      });
      strokePath(ctx, body, INK, 0.03);
      const base = roundRect(-0.46, 0.3, 0.46, 0.44, 0.04);
      fillPath(ctx, base, CONE_BASE);
      strokePath(ctx, base, INK, 0.03);
      break;
    }
    case 'hazard': {
      const tri = roundedPolygon([[0, -0.44], [0.49, 0.41], [-0.49, 0.41]], 0.07);
      fillPath(ctx, tri, HAZARD_YELLOW);
      strokePath(ctx, tri, INK, 0.06);
      fillPath(ctx, polygon([[-0.05, -0.17], [0.05, -0.17], [0.03, 0.16], [-0.03, 0.16]]), INK);
      const dot = new Path2D();
      dot.arc(0, 0.27, 0.05, 0, Math.PI * 2);
      fillPath(ctx, dot, INK);
      break;
    }
    case 'stop': {
      const octagon = (r: number) => polygon(Array.from({length: 8}, (_, i) => {
        const a = (22.5 + 45 * i) * Math.PI / 180;
        return [r * Math.cos(a), r * Math.sin(a)] as [number, number];
      }));
      fillPath(ctx, octagon(0.49), WHITE);
      fillPath(ctx, octagon(0.45), STOP_RED);
      handPath = handPath ?? new Path2D(HAND_PATH);
      ctx.save();
      ctx.translate(0, 0.01);
      ctx.scale(1.15, 1.15);
      ctx.scale(0.5 / 24, 0.5 / 24);
      ctx.translate(-12, -12);
      fillPath(ctx, handPath, WHITE);
      ctx.restore();
      break;
    }
  }
};

export class MarkupRenderer {
  /** One short-side unit, in photo pixels. */
  readonly unit: number;

  constructor(readonly width: number, readonly height: number, private readonly measure: Measure = canvasMeasure) {
    this.unit = Math.min(width, height);
  }

  px = (nx: number) => nx * this.width;
  py = (ny: number) => ny * this.height;

  // ---- sizes -------------------------------------------------------------

  strokeWidth = (size: MarkSize) => this.unit * {s: 0.006, m: 0.011, l: 0.02}[size];
  lineWidth = (size: MarkSize) => this.unit * {s: 0.009, m: 0.015, l: 0.024}[size];
  textSize = (size: MarkSize) => this.unit * {s: 0.045, m: 0.065, l: 0.095}[size];

  /** A freehand line's width (photo pixels): its step's divided by the zoom it was drawn at. */
  strokeWidthOf = (m: StrokeMark) => this.strokeWidth(m.size) / addedZoom(m.zoom);

  /** A text mark's letter size (photo pixels). */
  textSizeOf = (m: TextMark) => this.textSize(m.size) / addedZoom(m.zoom);

  /** A text mark's wrap width (photo pixels). */
  textWidthOf = (m: TextMark) => m.width * this.unit;

  // ---- drawing -----------------------------------------------------------

  /**
   * `showPlaceholders`: the editor shows an empty text box as a faint
   * "Type here"; the saved copy passes false, and empty text draws nothing.
   */
  draw(ctx: CanvasRenderingContext2D, marks: Mark[], showPlaceholders = false, skipId?: string | null) {
    marks.forEach(m => {
      if (m.id !== skipId) this.drawMark(ctx, m, showPlaceholders);
    });
  }

  drawMark(ctx: CanvasRenderingContext2D, mark: Mark, showPlaceholders = false) {
    switch (mark.type) {
      case 'stroke': return this.drawStroke(ctx, mark);
      case 'text': return this.drawText(ctx, mark, showPlaceholders);
      case 'sticker': return this.drawSticker(ctx, mark);
      case 'line': return this.drawLine(ctx, mark);
    }
  }

  private drawStroke(ctx: CanvasRenderingContext2D, m: StrokeMark) {
    const pts = m.points;
    if (pts.length < 2) return;
    const w = this.strokeWidthOf(m);
    const color = COLOR_HEX[m.color];
    if (pts.length < 4) {
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(this.px(pts[0]), this.py(pts[1]), w / 2, 0, Math.PI * 2);
      ctx.fill();
      return;
    }
    const path = new Path2D();
    path.moveTo(this.px(pts[0]), this.py(pts[1]));
    for (let i = 2; i + 1 < pts.length; i += 2) path.lineTo(this.px(pts[i]), this.py(pts[i + 1]));
    strokePath(ctx, path, color, w);
  }

  /** The text as laid out: wrapped at the box width, centered. */
  textLayout(m: TextMark, showPlaceholder = true): TextLayout {
    const size = this.textSizeOf(m);
    const width = Math.max(1, Math.floor(this.textWidthOf(m)));
    const text = m.text || (showPlaceholder ? PLACEHOLDER : '');
    const lines = wrapText(text, s => this.measure(s, size) <= width);
    const lineHeight = size * LINE_HEIGHT;
    return {lines, width, height: lines.length * lineHeight, size, lineHeight};
  }

  /** The box a text mark occupies, centered on its anchor, unrotated. */
  textBox(m: TextMark, showPlaceholder = true): Rect {
    const layout = this.textLayout(m, showPlaceholder);
    const w = this.textWidthOf(m);
    const ts = this.textSizeOf(m);
    const pad = ts * 0.25;
    const h = Math.max(layout.height, ts * 1.2) + pad * 2;
    return {left: -w / 2 - pad, top: -h / 2, right: w / 2 + pad, bottom: h / 2};
  }

  private drawText(ctx: CanvasRenderingContext2D, m: TextMark, showPlaceholder: boolean) {
    if (!m.text && !showPlaceholder) return;
    const layout = this.textLayout(m, showPlaceholder);
    const ts = layout.size;
    const placeholder = !m.text;
    ctx.save();
    ctx.translate(this.px(m.x), this.py(m.y));
    ctx.rotate(m.angle * Math.PI / 180);
    ctx.translate(-layout.width / 2, -layout.height / 2);
    ctx.font = `bold ${ts}px ${TEXT_FONT_FAMILY}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'alphabetic';
    // A thin black outline keeps every color readable over sky, bark and siding.
    ctx.lineJoin = 'round';
    ctx.lineWidth = ts * OUTLINE_TEXT;
    ctx.strokeStyle = OUTLINE;
    ctx.globalAlpha = placeholder ? 140 / 255 : 1;
    layout.lines.forEach((line, i) => ctx.strokeText(line, layout.width / 2, ASCENT * ts + i * layout.lineHeight));
    ctx.fillStyle = COLOR_HEX[m.color];
    ctx.globalAlpha = placeholder ? 150 / 255 : 1;
    layout.lines.forEach((line, i) => ctx.fillText(line, layout.width / 2, ASCENT * ts + i * layout.lineHeight));
    ctx.restore();
  }

  private drawSticker(ctx: CanvasRenderingContext2D, m: StickerMark) {
    const s = m.scale * this.unit;
    ctx.save();
    ctx.translate(this.px(m.x), this.py(m.y));
    if (STICKERS[m.kind].rotatable) ctx.rotate(m.angle * Math.PI / 180);
    ctx.scale(s, s);
    drawStickerUnit(ctx, m.kind, COLOR_HEX[m.color]);
    ctx.restore();
  }

  private drawLine(ctx: CanvasRenderingContext2D, m: LineMark) {
    const sw = this.lineWidth(m.size);
    const ax = this.px(m.x1);
    const ay = this.py(m.y1);
    const bx = this.px(m.x2);
    const by = this.py(m.y2);
    const len = Math.max(0.001, Math.hypot(bx - ax, by - ay));
    const dx = (bx - ax) / len;
    const dy = (by - ay) / len;
    const halo = sw * 0.45;
    const path = new Path2D();
    let fill: Path2D | null = null;
    if (m.kind === 'arrow') {
      const head = Math.min(sw * 4.5, len * 0.6);
      const half = head * 0.6;
      const baseX = bx - dx * head;
      const baseY = by - dy * head;
      path.moveTo(ax, ay);
      path.lineTo(baseX + dx * head * 0.3, baseY + dy * head * 0.3);
      fill = polygon([[bx, by], [baseX - dy * half, baseY + dx * half], [baseX + dy * half, baseY - dx * half]]);
    } else {
      const bar = sw * 3.2;
      path.moveTo(ax, ay);
      path.lineTo(bx, by);
      path.moveTo(ax - dy * bar, ay + dx * bar);
      path.lineTo(ax + dy * bar, ay - dx * bar);
      path.moveTo(bx - dy * bar, by + dx * bar);
      path.lineTo(bx + dy * bar, by - dx * bar);
    }
    strokePath(ctx, path, OUTLINE, sw + halo * 2);
    if (fill) {
      strokePath(ctx, fill, OUTLINE, halo * 2);
      fillPath(ctx, fill, OUTLINE);
    }
    strokePath(ctx, path, COLOR_HEX[m.color], sw);
    if (fill) fillPath(ctx, fill, COLOR_HEX[m.color]);
  }

  // ---- hit testing (photo pixels) -----------------------------------------

  /** The topmost mark under (x, y), with `tolerance` photo pixels of slack. */
  hitTest(marks: Mark[], x: number, y: number, tolerance: number): Mark | null {
    for (let i = marks.length - 1; i >= 0; i--) {
      if (this.hits(marks[i], x, y, tolerance)) return marks[i];
    }
    return null;
  }

  hits(mark: Mark, x: number, y: number, tolerance: number): boolean {
    switch (mark.type) {
      case 'stroke': {
        const p = mark.points;
        const reach = tolerance + this.strokeWidthOf(mark) / 2;
        if (p.length >= 2 && p.length <= 3) return Math.hypot(this.px(p[0]) - x, this.py(p[1]) - y) <= reach;
        for (let i = 0; i + 3 < p.length; i += 2) {
          if (distToSegment(x, y, this.px(p[i]), this.py(p[i + 1]), this.px(p[i + 2]), this.py(p[i + 3])) <= reach) return true;
        }
        return false;
      }
      case 'line':
        return distToSegment(x, y, this.px(mark.x1), this.py(mark.y1), this.px(mark.x2), this.py(mark.y2)) <=
          tolerance + this.lineWidth(mark.size) * 2;
      case 'text': {
        const local = toLocal(x, y, this.px(mark.x), this.py(mark.y), mark.angle);
        return rectContains(inset(this.textBox(mark), -tolerance), local.x, local.y);
      }
      case 'sticker': {
        const local = toLocal(x, y, this.px(mark.x), this.py(mark.y), stickerAngle(mark));
        return rectContains(inset(this.localBox(mark)!, -tolerance), local.x, local.y);
      }
    }
  }

  /** The unrotated box a box-shaped mark occupies around its anchor. */
  localBox(mark: Mark): Rect | null {
    if (mark.type === 'text') return this.textBox(mark);
    if (mark.type === 'sticker') {
      const half = mark.scale * this.unit / 2;
      // Arrows and cut lines are long and thin: a box to match.
      const tall = mark.kind === 'arrow' || mark.kind === 'cut' ? half * 0.5 : half;
      return {left: -half, top: -tall, right: half, bottom: tall};
    }
    return null;
  }

  /**
   * Photo-pixel bounds of a freehand stroke (its points plus half its width
   * each side), for its selection frame and the eraser's quick reject.
   */
  strokeBounds(m: StrokeMark): Rect {
    const p = m.points;
    if (p.length < 2) return {left: 0, top: 0, right: 0, bottom: 0};
    let l = this.px(p[0]);
    let t = this.py(p[1]);
    let r = l;
    let b = t;
    for (let i = 2; i + 1 < p.length; i += 2) {
      const x = this.px(p[i]);
      const y = this.py(p[i + 1]);
      l = Math.min(l, x);
      r = Math.max(r, x);
      t = Math.min(t, y);
      b = Math.max(b, y);
    }
    const pad = this.strokeWidthOf(m) / 2;
    return {left: l - pad, top: t - pad, right: r + pad, bottom: b + pad};
  }
}

export const colorHex = (c: MarkColor) => COLOR_HEX[c];
